// Prix Cardmarket servis par TCGdex, déjà présents dans chaque carte
// (`pricing.cardmarket`) — aucun appel réseau supplémentaire.
//
// TCGdex expose deux jeux de champs : les normaux (`trend`, `avg30`…) et les
// holographiques (`trend-holo`, `avg30-holo`…). Reverse et holo se négocient au
// second, comme dans le tableau Standard/Reverse de la fiche carte.
// ⚠️ TCGdex ne sert que DEUX niveaux de prix : le normal et l'holographique.
// Il n'existe ni cote « reverse » ni cote « 1re édition » séparées — Cardmarket
// en fait des produits distincts que TCGdex n'expose pas. Reverse et holo se
// lisent donc tous deux sur les champs `-holo`, au plus près de la réalité.
const HOLO_LIKE = new Set(["reverse", "holo", "cosmos", "rgb"]);

// Un 0 renvoyé par Cardmarket signifie « pas de cote », pas « gratuit » :
// on le traite comme une absence, sinon la moitié des tuiles affichait 0,00 €.
const num = (v) => (typeof v === "number" && v > 0 ? v : null);

const pick = (cm, base, variant) => {
  if (!cm) return null;
  const key = HOLO_LIKE.has(variant) ? `${base}-holo` : base;
  return num(cm[key]) ?? (key === base ? null : num(cm[base]));
};

/**
 * Prix tendance du variant demandé (« normal » par défaut).
 * Replis successifs : tendance -> moyenne -> plus bas. Beaucoup de cartes
 * anciennes n'ont pas de tendance mais ont bien une moyenne ; sans ce repli
 * elles paraissaient sans valeur.
 */
export const trendPrice = (card, variant = "normal") => {
  const cm = card?.pricing?.cardmarket;
  return pick(cm, "trend", variant) ?? pick(cm, "avg", variant) ?? pick(cm, "low", variant);
};

/** Moyenne 30 jours du même variant — point de comparaison des hausses. */
export const avg30Price = (card, variant = "normal") =>
  pick(card?.pricing?.cardmarket, "avg30", variant);

/**
 * Prix le plus élevé parmi les variantes affichées sur une tuile.
 * Une tuile montre plusieurs badges (normal + reverse) : afficher le prix le
 * plus fort évite de sous-estimer une carte dont seule la reverse a de la valeur.
 */
export const tileTrendPrice = (card, variants) => {
  const types = (variants || []).map(v => v.type).filter(t => t && t !== "loading");
  const prices = (types.length ? types : ["normal"])
    .map(t => trendPrice(card, t))
    .filter(p => typeof p === "number");
  return prices.length ? Math.max(...prices) : null;
};

/**
 * Étiquette du 2e niveau de prix (celui qui lit les champs `-holo`), à partir
 * des types de variantes réellement présents sur la carte — jamais du prix
 * lui-même. Les cartes à Première Édition (vieux tirages WOTC) n'ont pas de
 * reverse : leur variante `holo` sert alors de support à la cote 1re Édition
 * plutôt qu'à une vraie reverse holo, d'où la priorité `reverse` >
 * `firstEdition` (+ `holo`) > `holo` simple.
 */
export const secondaryPriceLabel = (types) => {
  if (types.includes("reverse")) return "reverse";
  if (types.includes("firstEdition") && types.includes("holo")) return "firstEdition";
  if (types.includes("cosmos") && !types.includes("holo")) return "cosmos";
  return "holo";
};

/**
 * Prix des variantes affichées sur une tuile, séparés plutôt que réduits au
 * plus élevé : une carte avec normal ET un 2e niveau (les deux seuls que sert
 * TCGdex, cf. en-tête du fichier) obtient 2 pastilles distinctes, une carte à
 * un seul niveau garde une seule pastille comme avant.
 */
export const tileTrendPrices = (card, variants) => {
  const types = (variants || []).map(v => v.type).filter(t => t && t !== "loading");
  const normal = types.includes("normal") ? trendPrice(card, "normal") : null;
  const holoType = types.find(t => HOLO_LIKE.has(t));
  const holo = holoType ? trendPrice(card, holoType) : null;
  if (typeof normal === "number" && typeof holo === "number" && normal !== holo) {
    return [{ key: "normal", value: normal }, { key: secondaryPriceLabel(types), value: holo }];
  }
  const single = tileTrendPrice(card, variants);
  if (typeof single !== "number") return [];
  // Une carte à Première Édition classique (vieux tirage WOTC) n'a le plus
  // souvent PAS de variante "normal" du tout (juste 1re Édition + holo) : le
  // test normal+holo ci-dessus ne se déclenche donc jamais pour elle, et elle
  // retombait sur la pastille grise générique "single" sans jamais refléter
  // sa vraie nature. Étiquetée à part ici pour recevoir la même couleur
  // dédiée que les cartes à 2 pastilles (cf. PRICE_TILE_STYLE, PokemonCardTile).
  if (types.includes("firstEdition")) {
    return [{ key: "firstEdition", value: single }];
  }
  return [{ key: "single", value: single }];
};

/** Montant en euros, format français. Renvoie null si pas de prix. */
export const eur = (value) =>
  typeof value === "number"
    ? `${value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
    : null;
